import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReservationForm from '../components/ReservationForm';
import PoweredBy from '../components/PoweredBy';
import { getRestaurantById } from '../services/restaurantService';
import { getEventById, getEventBySlug } from '../services/eventService';
import { normalizeNoteHtml } from '../utils/richText';
import { setPageSeo } from '../utils/seo';
import './ReservationWidget.css';
import '../styles/experience-page-v2.css';

/**
 * ExperiencePage — dedicated full-page booking page for a single experience.
 *
 * Route: /ex/:slug  (slug = events.slug, the canonical public URL)
 * Legacy: /experience/:id still resolves and redirects here once loaded, so
 *         links already sent out in emails, tickets and QR codes keep working.
 *
 * The internal `<ReservationForm singleEvent />` still runs the entire proven
 * booking flow — per-experience rules (require all guests' data, guest name
 * capture, house-rules toggle, min/max quantities, ticket-holder OTP, etc.)
 * all stay in place. This wrapper only owns the visual shell (hero, meta,
 * event notes) and the .experience-page-v2 CSS overlay that restyles what
 * the form renders. We intentionally do NOT touch ReservationForm's logic.
 */

// "21:00 - 23:00" (24h) → "9:00 PM – 11:00 PM" (12h)
const fmtOneTime = (t) => {
  if (!t) return '';
  const [h, m] = t.trim().split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return t;
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const h12 = hr % 12 || 12;
  return `${h12}:${m ?? '00'} ${ampm}`;
};

const formatEventTime = (event) => {
  if (event.startTime) {
    const start = fmtOneTime(event.startTime);
    const end = event.endTime ? fmtOneTime(event.endTime) : '';
    return end ? `${start} – ${end}` : start;
  }
  return event.time || '';
};

const ExperiencePage = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (!slug && !id) {
      setError('Experience not found.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = slug ? await getEventBySlug(slug) : await getEventById(id);
        if (cancelled) return;
        if (res.accessDenied) {
          setAccessDenied(res.message || "This is a private experience. You don't have access.");
          setLoading(false);
          return;
        }
        if (!res.event) {
          setError('This experience could not be found.');
          setLoading(false);
          return;
        }
        // Legacy /experience/:id entry point: swap the URL for the canonical
        // /ex/:slug now that the response told us the slug. `replace` keeps the
        // old URL out of the back-button history.
        if (!slug && res.event.slug) {
          navigate(`/ex/${res.event.slug}`, { replace: true });
          return;
        }
        setEvent(res.event);
        try {
          const r = await getRestaurantById(res.event.restaurantId);
          if (!cancelled) setRestaurant(r);
        } catch {
          if (!cancelled) setRestaurant(res.event.restaurant || null);
        }
        setError(null);
      } catch (err) {
        console.error('Error loading experience:', err);
        if (!cancelled) setError('Something went wrong loading this experience.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, slug, navigate]);

  // Per-page SEO once the experience is loaded. The canonical is always
  // /ex/:slug, also when the page was reached through /experience/:id.
  useEffect(() => {
    if (!event) return;
    const description = String(event.description || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#0*39;|&apos;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 200);
    setPageSeo({
      title: `${event.name} — ${restaurant?.name || 'ez restaurants'}`,
      description,
      canonicalPath: event.slug ? `/ex/${event.slug}` : undefined,
    });
  }, [event, restaurant]);

  if (loading) {
    return (
      <div className="expv2-loading">
        <div className="expv2-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  if (accessDenied) {
    const isLoggedIn = !!localStorage.getItem('eez7_client_token');
    return (
      <div className="expv2-error">
        <div className="expv2-error-icon"><i className="bi bi-lock-fill" /></div>
        <h3>Private experience</h3>
        <p>{accessDenied}</p>
        {!isLoggedIn && (
          <a
            href={`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="expv2-btn"
          >
            Sign in to check access
          </a>
        )}
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="expv2-error">
        <div className="expv2-error-icon"><i className="bi bi-emoji-frown" /></div>
        <p>{error || 'Experience not found'}</p>
      </div>
    );
  }

  const heroImg = event.imageUrl || event.mobileCoverImageUrl;
  const floorPlanImg = event.floorPlanUrl || null;
  const dateText = event.dateFormatted || event.date || '';
  const timeText = formatEventTime(event);
  const houseRules = event.houseRules;
  const description = event.description;

  return (
    <div className="expv2-page">
      <div className="expv2-card">

        {/* Hero — the artwork, nothing on top of it. The banner already carries
            the act's name and the venue, so the old overlay (badge + title +
            location) repeated that text and covered the photo. The name now
            sits below the Date/Time strip instead. Hidden after successful
            booking so the confirmation screen owns the viewport. */}
        {!showSuccess && heroImg && (
          <div className="expv2-hero">
            <img
              src={heroImg}
              alt={event.title || event.name}
              className="expv2-hero-img"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Meta strip — Date + Time only. Capacity intentionally not shown. */}
        {!showSuccess && (dateText || timeText) && (
          <div className="expv2-meta">
            {dateText && (
              <div className="expv2-meta-cell">
                <i className="bi bi-calendar-event" />
                <span className="k">Date</span>
                <span className="v">{dateText}</span>
              </div>
            )}
            {timeText && (
              <div className="expv2-meta-cell">
                <i className="bi bi-clock" />
                <span className="k">Time</span>
                <span className="v">{timeText}</span>
              </div>
            )}
          </div>
        )}

        {/* Experience name. Below Date/Time rather than over the hero — see the
            hero comment above. */}
        {!showSuccess && (
          <h1 className="expv2-title">{event.title || event.name}</h1>
        )}

        <div className="expv2-body">

          {/* Floor plan — optional venue layout uploaded by the admin.
              Sits between the Date/Time strip and the description so
              guests see the layout (VIP lounges, bar, stage, entrance)
              before they read the rules and pick tickets. */}
          {!showSuccess && floorPlanImg && (
            <figure className="expv2-floorplan">
              <img
                src={floorPlanImg}
                alt="Venue floor plan"
                loading="lazy"
              />
            </figure>
          )}

          {/* Description — plain content block, no title. Admin's free-text /
              rich-HTML gets rendered as-is. */}
          {!showSuccess && description && (
            <div
              className="expv2-content"
              dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(description) }}
            />
          )}

          {/* House rules — plain content block, no title, warning accent so it
              reads as an advisory. */}
          {!showSuccess && houseRules && houseRules.replace(/<[^>]*>/g, '').trim() && (
            <div
              className="expv2-content expv2-content-warning"
              dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(houseRules) }}
            />
          )}

          {/* The booking form runs the full flow — per-experience rules, guest
              collection, min/max, OTP, holder cards, etc. all still apply. */}
          <div className="expv2-form-wrap">
            <ReservationForm
              key={formKey}
              singleEvent
              restaurantId={event.restaurantId}
              restaurantName={restaurant?.name || event.restaurant?.name || ''}
              restaurant={restaurant || event.restaurant || null}
              isWidget={true}
              initialEvent={event}
              onSuccessChange={setShowSuccess}
              onRestart={() => { setShowSuccess(false); setFormKey((k) => k + 1); }}
            />
          </div>
        </div>

      </div>
      <PoweredBy hidden={!!restaurant?.organization?.hidePoweredBy} />
    </div>
  );
};

export default ExperiencePage;
